const clone = value => JSON.parse(JSON.stringify(value));
const text = value => typeof value === "string" ? value : "";
const affiliations = value => Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
export const METADATA_PENDING_STATES = new Set(["awaiting_upload", "queued", "processing"]);

export function normalizeMetadataTag(value) {
  const label = text(value).normalize("NFC").replace(/\s+/gu, " ").trim();
  const points = Array.from(label);
  const bytes = points.reduce((total, point) => total + (point.codePointAt(0) <= 0x7f ? 1 : point.codePointAt(0) <= 0x7ff ? 2 : point.codePointAt(0) <= 0xffff ? 3 : 4), 0);
  if (points.length > 120 || bytes > 512 || /[\u0000-\u001f\u007f]/u.test(label)) throw new Error("metadata-tag");
  return label;
}

export function addMetadataTag(subjects, value) {
  const subject = normalizeMetadataTag(value);
  if (!subject || subjects.some(row => normalizeMetadataTag(row.subject).toLowerCase().normalize("NFC") === subject.toLowerCase().normalize("NFC"))) return subjects;
  if (subjects.length >= 100) throw new Error("metadata-tag");
  return [...subjects, { subject }];
}

export function metadataTagSuggestions(candidates, subjects) {
  const seen = new Set(subjects.map(row => text(row.subject).normalize("NFC").replace(/\s+/gu, " ").trim().toLowerCase().normalize("NFC")));
  return (Array.isArray(candidates) ? candidates : []).filter(row => {
    let subject;
    try { subject = normalizeMetadataTag(row?.subject); } catch { return false; }
    if (!subject || seen.has(subject.toLowerCase().normalize("NFC"))) return false;
    seen.add(subject.toLowerCase().normalize("NFC"));
    return true;
  });
}

export function createMetadataDraft(metadata = {}) {
  const descriptive = metadata?.descriptive || {};
  const experiment = metadata?.experiment || {};
  return {
    title: text(descriptive.titles?.[0]?.title),
    description: text(descriptive.descriptions?.[0]?.description),
    subjects: clone(descriptive.subjects || []), tagInput: "",
    creators: clone(descriptive.creators || []).map(row => ({ ...row, _affiliations: affiliations(row.affiliation).map(item => typeof item === "string" ? item : item?.name).filter(Boolean).join("\n") })),
    contributors: clone(descriptive.contributors || []).map(row => ({ ...row, _affiliations: affiliations(row.affiliation).map(item => typeof item === "string" ? item : item?.name).filter(Boolean).join("\n") })),
    identifiers: clone(descriptive.identifiers || []),
    rightsList: clone(descriptive.rightsList || []),
    method: text(experiment.method), instrument: text(experiment.instrument), specimen: text(experiment.specimen), conditions: text(experiment.conditions),
    physicalProperties: clone(metadata?.physicalProperties || []).map(row => ({ ...row, value: row.value ?? "", unit: row.unit ?? "", _original: { value: row.value, unit: row.unit } })),
  };
}

// Send only fields the user edited. Existing untranslated source text, DataCite
// extensions, additional titles, and unrelated canonical fields are preserved.
export function buildMetadataPatch(draft, changed, metadata = {}) {
  const set = {}, descriptive = {}, experiment = {};
  const previous = metadata?.descriptive || {};
  if (changed.has("title")) {
    const rest = clone(previous.titles || []).slice(1);
    descriptive.titles = draft.title.trim() ? [{ ...previous.titles?.[0], title: draft.title.trim() }, ...rest] : rest;
  }
  if (changed.has("description")) {
    const rest = clone(previous.descriptions || []).slice(1);
    descriptive.descriptions = draft.description.trim() ? [{ descriptionType: "Other", ...previous.descriptions?.[0], description: draft.description.trim() }, ...rest] : rest;
  }
  if (changed.has("subjects")) {
    const subjects = addMetadataTag(draft.subjects, draft.tagInput);
    if (subjects.length > 100) throw new Error("metadata-tag");
    // Existing controlled-vocabulary identities and attribution stay attached to
    // their rows. Only newly authored tags are plain subject objects.
    descriptive.subjects = clone(subjects);
  }
  for (const people of ["creators", "contributors"]) if (changed.has(people)) descriptive[people] = draft[people].filter(row => row.name?.trim() || row._affiliations?.trim()).map(({ _affiliations, ...row }) => {
    if (!row.name?.trim()) throw new Error("creator-name");
    const existing = affiliations(row.affiliation);
    return { ...row, name: row.name.trim(), affiliation: (_affiliations || "").split("\n").map(name => name.trim()).filter(Boolean).map(name => existing.find(item => item?.name === name) || { name }) };
  });
  if (changed.has("identifiers")) descriptive.identifiers = draft.identifiers.filter(row => row.identifier?.trim()).map(row => ({ ...row, identifier: row.identifier.trim(), identifierType: row.identifierType || "URL" }));
  if (changed.has("rightsList")) descriptive.rightsList = draft.rightsList.filter(row => row.rights?.trim() || row.rightsURI?.trim()).map(row => ({ ...row, rights: (row.rights || "").trim(), rightsURI: (row.rightsURI || "").trim() }));
  for (const field of ["method", "instrument", "specimen", "conditions"]) if (changed.has(field)) experiment[field] = draft[field].trim();
  if (Object.keys(descriptive).length) set.descriptive = descriptive;
  if (Object.keys(experiment).length) set.experiment = experiment;
  if (changed.has("physicalProperties")) set.physicalProperties = draft.physicalProperties.map(({ _original, ...row }) => {
    const value = row.value === "" ? null : Number(row.value);
    if (!row.property?.trim() || !row.scope?.trim() || (value !== null && !Number.isFinite(value))) throw new Error("physical-property");
    const result = { ...row, property: row.property.trim(), scope: row.scope.trim(), value, unit: row.unit?.trim() || null, origin: row.origin || "human-reviewed" };
    if (_original?.value !== value || _original?.unit !== result.unit) { delete result.normalizedValue; delete result.normalizedUnit; }
    return result;
  });
  return { set };
}
